import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { Icon } from '../icons';
import { foldRepeats, highlightParts, matchingLines, normalizeLevel } from './logText';
import type { Row, ViewerLine } from './logText';

export type { ViewerLine as LogLine } from './logText';

/**
 * LogViewer (CO-150): a run's raw output, kept apart from the interpreted
 * summary above it. A bounded dark surface with its own scrolling, line
 * numbers, preserved whitespace, search with match navigation (Enter and
 * Shift+Enter), copy, and a wrap toggle (off: the lines scroll sideways).
 * Errors and warnings are marked only on a clear sign. Runs of lines that
 * differ only in their numbers (a package manager's progress) are folded,
 * one click from being shown. Secrets are redacted on the server before
 * any line reaches here.
 *
 * `LogPanel` is this component under its earlier name.
 */
export function LogViewer({
  title = 'Log',
  lines,
  toolbar,
  empty = 'Nothing logged yet.',
  live = false,
  testId,
  initialQuery = '',
  defaultWrap = true,
  maxHeight = 'default',
  headingLevel = 3,
}: {
  title?: ReactNode;
  lines: ViewerLine[];
  /** Filters that belong to this log (a step, a level). */
  toolbar?: ReactNode;
  empty?: ReactNode;
  /** Announce how many lines there are, politely and not on every line, while a run is in progress. The lines themselves are never announced. */
  live?: boolean;
  testId?: string;
  /** Opens with this search (an error code), scrolled to its first match. */
  initialQuery?: string;
  defaultWrap?: boolean;
  maxHeight?: 'default' | 'short';
  headingLevel?: 3 | 4;
}) {
  const titleId = useId();
  const searchId = useId();
  const [query, setQuery] = useState(initialQuery);
  const [wrap, setWrap] = useState(defaultWrap);
  const [current, setCurrent] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState<'idle' | 'done' | 'failed'>('idle');
  const bodyRef = useRef<HTMLPreElement>(null);
  // While a run streams, what is announced is the count, a few seconds apart:
  // the log body is not a live region, so a fold button inside it is never
  // part of an announcement and a screen reader is not read every line
  // (CO-184, DS6).
  const [announcedCount, setAnnouncedCount] = useState(lines.length);
  useEffect(() => {
    if (!live) return undefined;
    const timer = window.setTimeout(() => setAnnouncedCount(lines.length), 3000);
    return () => window.clearTimeout(timer);
  }, [live, lines.length]);

  useEffect(() => setQuery(initialQuery), [initialQuery]);

  const matches = useMemo(() => matchingLines(lines, query), [lines, query]);
  const matchSet = useMemo(() => new Set(matches), [matches]);
  const currentLine = matches.length > 0 ? matches[Math.min(current, matches.length - 1)]! : -1;
  // A search shows every line; folding is for reading, not for finding.
  const rows: Row[] = useMemo(() => (query.trim() ? lines.map((line, index) => ({ kind: 'line' as const, line, number: index + 1 })) : foldRepeats(lines)), [lines, query]);

  useEffect(() => setCurrent(0), [query]);

  // Bring the current match into view inside the log's own box, never by
  // scrolling the page.
  useEffect(() => {
    if (currentLine < 0 || !query.trim()) return;
    const body = bodyRef.current;
    const target = body?.querySelector<HTMLElement>(`[data-line="${currentLine + 1}"]`);
    if (body && target) body.scrollTop = Math.max(0, target.offsetTop - body.clientHeight / 3);
  }, [currentLine, query]);

  const step = (direction: 1 | -1) => {
    if (matches.length === 0) return;
    setCurrent((value) => (value + direction + matches.length) % matches.length);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(lines.map((line) => [line.at, line.source, line.text].filter(Boolean).join('  ')).join('\n'));
      setCopied('done');
    } catch {
      setCopied('failed');
    }
    window.setTimeout(() => setCopied('idle'), 2500);
  };

  const Heading = headingLevel === 3 ? 'h3' : 'h4';
  const renderLine = (line: ViewerLine, number: number) => {
    const level = normalizeLevel(line.level);
    const isCurrent = number - 1 === currentLine;
    return (
      <span
        key={line.key}
        className={`log-viewer__line${level ? ` log-viewer__line--${level}` : ''}${matchSet.has(number - 1) ? ' log-viewer__line--match' : ''}${isCurrent ? ' log-viewer__line--current' : ''}`}
        data-line={number}
      >
        <span className="log-viewer__number" aria-hidden="true">
          {number}
        </span>
        {line.at ? <span className="log-viewer__at">{line.at}</span> : null}
        {line.source ? <span className="log-viewer__source">{line.source}</span> : null}
        <span className="log-viewer__text">
          {highlightParts(line.text, query).map((part, index) =>
            part.match ? (
              <mark key={index} className="log-viewer__mark">
                {part.text}
              </mark>
            ) : (
              part.text
            ),
          )}
        </span>
        {'\n'}
      </span>
    );
  };

  return (
    <section className="log-viewer" aria-labelledby={titleId} data-testid={testId}>
      <header className="log-viewer__header">
        <Heading id={titleId} className="log-viewer__title">
          {title}
        </Heading>
        <div className="log-viewer__tools">
          {toolbar}
          <label className="log-viewer__search" htmlFor={searchId}>
            <span className="visually-hidden">Search the log</span>
            <Icon name="search" size={14} />
            <input
              id={searchId}
              type="search"
              value={query}
              placeholder="Search…"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  step(event.shiftKey ? -1 : 1);
                }
              }}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          {query.trim() ? (
            <span className="log-viewer__count" aria-live="polite" data-testid="log-match-count">
              {matches.length === 0 ? 'No match' : `${Math.min(current, matches.length - 1) + 1} of ${matches.length}`}
            </span>
          ) : null}
          {query.trim() && matches.length > 1 ? (
            <>
              <button type="button" className="log-viewer__tool" onClick={() => step(-1)} aria-label="Previous match">
                <Icon name="chevron-up" size={16} />
              </button>
              <button type="button" className="log-viewer__tool" onClick={() => step(1)} aria-label="Next match">
                <Icon name="chevron-down" size={16} />
              </button>
            </>
          ) : null}
          <button type="button" className="log-viewer__tool" aria-pressed={wrap} onClick={() => setWrap((value) => !value)} title="Wrap long lines">
            <Icon name="wrap-text" size={16} />
            <span className="visually-hidden">Wrap long lines</span>
          </button>
          <button type="button" className="log-viewer__tool" onClick={copy} disabled={lines.length === 0} title="Copy the whole log">
            <Icon name={copied === 'done' ? 'check' : 'copy'} size={16} />
            <span className="visually-hidden">Copy the whole log</span>
          </button>
          <span className="visually-hidden" role="status">
            {copied === 'done' ? 'Log copied.' : copied === 'failed' ? 'The log could not be copied.' : ''}
          </span>
        </div>
      </header>
      {/* The one live region of the viewer: it exists before the first line, so the count that arrives later is announced. */}
      <p className="visually-hidden" role="status" aria-live="polite" data-testid="log-line-count">
        {live && announcedCount > 0 ? `${announcedCount} ${announcedCount === 1 ? 'line' : 'lines'} so far.` : ''}
      </p>
      {lines.length === 0 ? (
        <div className="log-viewer__body log-viewer__body--empty" role="log" aria-live="off" aria-labelledby={titleId}>
          <p className="log-viewer__empty">{empty}</p>
        </div>
      ) : (
        <pre
          ref={bodyRef}
          className={`log-viewer__body${wrap ? ' log-viewer__body--wrap' : ''}${maxHeight === 'short' ? ' log-viewer__body--short' : ''}`}
          role="log"
          aria-live="off"
          aria-labelledby={titleId}
          tabIndex={0}
        >
          <code>
            {rows.map((row) =>
              row.kind === 'line' ? (
                renderLine(row.line, row.number)
              ) : expanded.has(row.key) ? (
                row.lines.map(({ line, number }) => renderLine(line, number))
              ) : (
                <span key={row.key} className="log-viewer__fold">
                  <button type="button" className="log-viewer__fold-button" onClick={() => setExpanded((value) => new Set(value).add(row.key))}>
                    Show {row.count} similar {row.count === 1 ? 'line' : 'lines'}
                  </button>
                  {'\n'}
                </span>
              ),
            )}
          </code>
        </pre>
      )}
    </section>
  );
}
